import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
/**
 * **Échange de conducteurs** (2026-09-13, contrat v1.52).
 *
 * Le véhicule A tombe en panne. Son conducteur avait une pause réglementaire
 * prévue ; l'immobilisation menace de la rogner. L'outil cherche, **sur la même
 * ligne**, un véhicule B qui va passer là où A est bloqué : le conducteur A
 * monterait dans B et poursuivrait, le conducteur B « prenant la panne » —
 * **à condition que B soit moins impacté que A**.
 *
 * Une pause se lit dans le service conducteur : c'est le trou entre la fin
 * d'une (ligne, sortie) et la prestation suivante. Le minimum légal est une
 * donnée de configuration (`thresholds.driverMinimumBreakSeconds`), corrigeable
 * à chaud — jamais une constante du code.
 *
 * **Sentinelle conseille, n'agit pas** : `GET` seul, aucune affectation, aucun
 * ordre transmis au SAE. Identification non nominative (CDC §7.3) : un
 * conducteur n'est désigné que par son **n° de service** et ses lettres de
 * groupe, jamais par un nom ou un matricule.
 */
/** Pause réglementaire d'un conducteur, telle qu'imprimée à son service. */
export const DriverBreakInfo = z.object({
    /** `null` = plus aucune pause à venir aujourd'hui. */
    startsAt: IsoDateTime.nullable(),
    endsAt: IsoDateTime.nullable(),
    durationSeconds: z.number().int().nullable(),
    /** Lieu où le conducteur quitte son véhicule. Libellé du document. */
    atPlaceRaw: z.string().nullable(),
    /**
     * Secondes pouvant être rognées avant de passer sous le minimum légal.
     * `null` s'il n'y a plus de pause (rien à protéger, rien à perdre).
     * Négatif = la pause est **déjà** sous le minimum.
     */
    slackSeconds: z.number().int().nullable(),
});
/** Le conducteur et le véhicule concernés — jamais d'identité nominative. */
export const DriverSwapParty = z.object({
    journeyId: OpaqueId,
    runId: z.string().nullable(),
    vehicleDisplayNumber: z.string().nullable(),
    /** N° de service conducteur (non nominatif). `null` si non rapproché. */
    serviceNumber: z.string().nullable(),
    groupLetters: z.string().nullable(),
    nextBreak: DriverBreakInfo,
});
export const DriverSwapCandidate = z.object({
    candidate: DriverSwapParty,
    /** Arrêt où B passe et où A est immobilisé — le point d'échange. */
    meetStopId: OpaqueId,
    meetStopName: z.string().nullable(),
    meetAt: IsoDateTime,
    /** Secondes avant que B n'y soit. Peut être légèrement négatif (imminent). */
    meetInSeconds: z.number().int(),
    /**
     * La pause du conducteur A est-elle préservée si l'échange a lieu ?
     * `false` = l'échange ne règle pas son problème.
     */
    preservesBrokenDriverBreak: z.boolean(),
    /**
     * Ce qui resterait de la pause du conducteur B après avoir « pris la panne ».
     * `null` si B n'a plus de pause à venir.
     */
    candidateBreakLeftSeconds: z.number().int().nullable(),
    /** B reste-t-il au-dessus du minimum légal ? `null` si B n'a plus de pause. */
    candidateStaysLegal: z.boolean().nullable(),
    /**
     * Lecture d'ensemble :
     * - `favorable` : A garde sa pause ET B reste légal ;
     * - `acceptable` : A garde sa pause, B n'a plus de pause à protéger ;
     * - `deconseille` : l'échange déplace le problème (B passerait sous le
     *   minimum) ou ne sauve pas la pause de A.
     */
    verdict: z.enum(["favorable", "acceptable", "deconseille"]),
});
export const DriverSwapResponse = z.object({
    at: IsoDateTime,
    /** Écho du seuil appliqué (`thresholds.driverMinimumBreakSeconds`). */
    minimumBreakSeconds: z.number().int(),
    lineId: OpaqueId,
    lineCode: z.string(),
    /** Le véhicule en panne et son conducteur. */
    brokenDown: DriverSwapParty,
    /** Candidats classés : `favorable` d'abord, puis rendez-vous le plus proche. */
    candidates: z.array(DriverSwapCandidate),
});
//# sourceMappingURL=driver-swap.js.map